import React, { useCallback, useEffect, useState } from 'react';

import { AuthProvider } from './hoc/Auth/';
import ErrorBoundary from './Components/ErrorBoundary';
import { PrivateRout } from './PrivateRoute';
import { Redirect, Route, Switch, useHistory, useLocation } from 'react-router-dom';
import { IntlProvider } from 'react-intl';
import ApiConfig from './ApiConfig';
import { isPublicPath } from './Services/publicPaths';

import Header from './Components/Header';
import Message from './Components/Utils/Message/message';
import { AdminLayout, PublicLayout, UserLayout } from './hoc/Layout';

// Public pages
import Login from './PagesOfCommon/Login';
import Confirmation from './PagesOfCommon/Сonfirmation';
import PublicPageRu from './PagesOfCommon/wiki';
import PublicPageEn from './PagesOfCommon/wiki/index.en';
import ApiV3DocsRedirect from './PagesOfCommon/ApiV3DocsRedirect';
import Pay3 from './PagesOfCommon/Pay3';
import Pay4 from './PagesOfCommon/Pay4';
import PayTransfer from './PagesOfCommon/PayTransfer';
import NPay from './PagesOfCommon/NPay';
import WidgetDocs from './PagesOfCommon/Widget';

// Users pages
import Dashboard from './PagesOfUser/Dashboard';
import RegistryUpload from './PagesOfUser/RegistryUpload';
import Pay from './PagesOfUser/Pay';
import Projects from './PagesOfUser/Projects';
import ProjectSettings from './PagesOfUser/ProjectSettings';
import Transactions from './PagesOfUser/Transactions';
import Payouts from './PagesOfUser/Payouts';
import PayoutsDetail from './PagesOfUser/PayoutsDetail';
import Refills from './PagesOfUser/Refills';
import Statement from './PagesOfUser/Statement';
import Profile from './PagesOfUser/Profile';
import SecretChats from './PagesOfUser/SecretChats';
import Prepaid from './PagesOfUser/Prepaid';
import PrepaidActivate from './PagesOfUser/PrepaidActivate';
import PrepaidDetails from './PagesOfUser/PrepaidDetails';
import UserAntiFraudCheck from './PagesOfUser/UserAntiFraudCheck';
import UserAntiFraudSingleCheck from './PagesOfUser/UserAntiFraudSingleCheck';
import AntiFraudLog from './PagesOfUser/AntiFraudLog';
import MobilePayouts from './PagesOfUser/MobilePayouts';
import MobileRegistryUpload from './PagesOfUser/MobileRegistryUpload';
import SbpPay from './PagesOfUser/SbpPay';

// Administrator pages
import Console from './PagesOfAdministrator/Console';
import AccountsControl from './PagesOfAdministrator/Accounts';
import ProjectsControl from './PagesOfAdministrator/Ptojects';
import Terminals from './PagesOfAdministrator/Terminals';
import ProfileControl from './PagesOfAdministrator/Profile';
import PayoutsStatistic from './PagesOfAdministrator/Payouts';
import ReportConstructor from './PagesOfAdministrator/ReportConstructor';
import Frod from './PagesOfAdministrator/Frod';
import ApiConsole from './PagesOfAdministrator/Postman';
import AdminAudit from './PagesOfAdministrator/AdminAudit';
import TransactionAudit from './PagesOfAdministrator/TransactionAudit';
import AntiFraud from './PagesOfAdministrator/AntiFraud';
import PayoutHolds from './PagesOfAdministrator/PayoutHolds';
import Disputes from './PagesOfAdministrator/Disputes';
import CrmPayoutRequests from './PagesOfAdministrator/CrmPayoutRequests';
import CrmSbpPayoutRequests from './PagesOfAdministrator/CrmSbpPayoutRequests';
import Settlement from './PagesOfAdministrator/Settlement';
import Deposit from './PagesOfAdministrator/Deposit';
import Reconciliation from './PagesOfAdministrator/Reconciliation';
import TrafficMonitoring from './PagesOfAdministrator/TrafficMonitoring';
import AdminTickets from './PagesOfAdministrator/Tickets';
import AdminSecretChats from './PagesOfAdministrator/SecretChats';
import CraftliteLimit from './PagesOfAdministrator/CraftliteLimit';

import MessageContext from './MessageContext';

import ru from './translation/ru.json';
import en from './translation/en.json';
import ContractsList from './PagesOfUser/Metal/ContractsList';
import ContractCreate from './PagesOfUser/Metal/ContractCreate';
import ContractEdit from './PagesOfUser/Metal/ContractEdit';
import PcaList from './PagesOfUser/Metal/PcaList';
import PcaEdit from './PagesOfUser/Metal/PcaEdit';
import ContractPca from './PagesOfUser/Metal/ContractPca';
import PcaCreate from './PagesOfUser/Metal/PcaCreate';
import PcaDuplicate from './PagesOfUser/Metal/PcaDuplicate';

const PAYMENT_PATHS = ['/pay3', '/pay4', '/PayTransfer', '/npay'];

const USER_CONTOUR_PATHS = [
    '/registry-upload',
    '/mobile-registry-upload',
    '/mobile-payouts',
    '/pay',
    '/sbp-pay',
    '/metal',
    '/antifraud-check',
    '/antifraud-single-check',
    '/antifraud-log',
    '/prepaid',
    '/transactions',
    '/payouts',
    '/projects',
    '/refills',
    '/profile',
    '/secret-chats',
    '/statement',
];

const ADMIN_CONTOUR_PATHS = [
    '/console',
    '/admin-audit',
    '/accounts-control',
    '/projects-control',
    '/terminals-control',
    '/profile-control',
    '/payouts-statistic',
    '/report-constructor',
    '/frod',
    '/antifraud',
    '/payout-holds',
    '/disputes',
    '/transaction-audit',
    '/settlements',
    '/deposits',
    '/reconciliation',
    '/traffic-monitoring',
    '/admin/tickets',
    '/admin/secret-chats',
    '/api-console',
    '/crm-payout-requests',
    '/crm-sbp-payout-requests',
    '/craftlite-limit',
];

function UserRoutes({ footerProps }) {
    return (
        <UserLayout footerProps={footerProps}>
            <PrivateRout exact path="/registry-upload" component={RegistryUpload} />
            <PrivateRout exact path="/mobile-registry-upload" component={MobileRegistryUpload} />
            <PrivateRout exact path="/mobile-payouts" component={MobilePayouts} />

            <PrivateRout exact path="/pay" component={Pay} />
            <PrivateRout path="/pay/:data" component={Pay} />
            <PrivateRout exact path="/sbp-pay" component={SbpPay} />

            <PrivateRout exact path="/metal" component={ContractsList} />
            <PrivateRout exact path="/metal/contract/create" component={ContractCreate} />
            <PrivateRout exact path="/metal/contract/:ID" component={ContractEdit} />
            <PrivateRout exact path="/metal/pca" component={PcaList} />
            <PrivateRout exact path="/metal/pca/:ID" component={PcaEdit} />
            <PrivateRout exact path="/metal/contract/:ID/pca" component={ContractPca} />
            <PrivateRout exact path="/metal/contract/:ID/pca/create" component={PcaCreate} />
            <PrivateRout exact path="/metal/pca/duplicate/:ID" component={PcaDuplicate} />

            <PrivateRout exact path="/antifraud-check" component={UserAntiFraudCheck} />
            <PrivateRout exact path="/antifraud-single-check" component={UserAntiFraudSingleCheck} />
            <PrivateRout exact path="/antifraud-log" component={AntiFraudLog} />

            <PrivateRout exact path="/prepaid" component={Prepaid} />
            <PrivateRout path="/prepaid/activate/:EAN" component={PrepaidActivate} />
            <PrivateRout path="/prepaid/details/:EAN" component={PrepaidDetails} />

            <PrivateRout exact path="/transactions" component={Transactions} />
            <PrivateRout path="/transactions/:filters" component={Transactions} />

            <PrivateRout exact path="/payouts" component={Payouts} />
            <PrivateRout path="/payouts/registry/:id" component={PayoutsDetail} />

            <PrivateRout exact path="/projects" component={Projects} />
            <PrivateRout path="/projects/settings/:uuid" component={ProjectSettings} />

            <PrivateRout path="/refills" component={Refills} />
            <PrivateRout path="/profile" component={Profile} />
            <PrivateRout path="/statement" component={Statement} />

            <PrivateRout exact path="/" component={Dashboard} />

            <Redirect to="/" />
        </UserLayout>
    );
}

function AdminRoutes({ footerProps }) {
    return (
        <AdminLayout footerProps={footerProps}>
            <PrivateRout path="/console" component={Console} />
            <PrivateRout path="/admin-audit" component={AdminAudit} />
            <PrivateRout path="/accounts-control" component={AccountsControl} />
            <PrivateRout path="/projects-control" component={ProjectsControl} />
            <PrivateRout path="/terminals-control" component={Terminals} />
            <PrivateRout path="/profile-control" component={ProfileControl} />
            <PrivateRout path="/payouts-statistic" component={PayoutsStatistic} />
            <PrivateRout path="/report-constructor" component={ReportConstructor} />
            <PrivateRout path="/frod" component={Frod} />
            <PrivateRout path="/antifraud" component={AntiFraud} />
            <PrivateRout exact path="/payout-holds" component={PayoutHolds} />
            <PrivateRout path="/disputes" component={Disputes} />
            <PrivateRout path="/transaction-audit" component={TransactionAudit} />
            <PrivateRout path="/settlements" component={Settlement} />
            <PrivateRout path="/deposits" component={Deposit} />
            <PrivateRout path="/reconciliation" component={Reconciliation} />
            <PrivateRout path="/traffic-monitoring" component={TrafficMonitoring} />
            <PrivateRout path="/secret-chats" component={SecretChats} />
            <PrivateRout exact path="/admin/secret-chats" component={AdminSecretChats} />
            <PrivateRout exact path="/admin/tickets" component={AdminTickets} />
            <PrivateRout exact path="/admin/tickets/:id" component={AdminTickets} />
            <PrivateRout exact path="/api-console" component={ApiConsole} />
            <PrivateRout exact path="/crm-payout-requests" component={CrmPayoutRequests} />
            <PrivateRout exact path="/crm-sbp-payout-requests" component={CrmSbpPayoutRequests} />
            <PrivateRout exact path="/craftlite-limit" component={CraftliteLimit} />

            <Redirect to="/console" />
        </AdminLayout>
    );
}

function App() {
    const [lang, setLang] = useState('ru');
    const [translate, setTranslate] = useState(ru);
    const [message, setMessage] = useState({ type: null, message: '' });

    const location = useLocation();
    const history = useHistory();
    const footerProps = { lang, onChangeLanguage };

    const isPaymentLayout = PAYMENT_PATHS.some((path) => location.pathname.startsWith(path));
    const isWidgetPage = location.pathname.startsWith('/pay3') || location.pathname.startsWith('/npay');

    useEffect(() => {
        ApiConfig.setRedirectCallback((path) => {
            // С открытых страниц не уводим: протухшая сессия гасится, но человек
            // остаётся там, куда пришёл (например, на подтверждении почты).
            if (isPublicPath(history.location.pathname)) {
                return;
            }
            history.push(path);
        });
    }, [history]);

    useEffect(() => {
        const language =
            window.navigator &&
            (window.navigator.language || window.navigator.systemLanguage || window.navigator.userLanguage);

        if (!localStorage.getItem('language')) {
            switch (language.substr(0, 2).toLowerCase()) {
                case 'ru':
                    localStorage.setItem('language', 'ru');
                    setLang('ru');
                    setTranslate(ru);
                    break;
                default:
                    localStorage.setItem('language', 'en');
                    setLang('en');
                    setTranslate(en);
            }
        } else {
            switch (localStorage.getItem('language')) {
                case 'ru':
                    setLang('ru');
                    setTranslate(ru);
                    break;
                default:
                    setLang('en');
                    setTranslate(en);
            }
        }
    }, [lang]);

    function onChangeLanguage(selectedLang) {
        if (selectedLang === 'ru') {
            setLang('ru');
            setTranslate(ru);
            localStorage.setItem('language', 'ru');
        } else {
            setLang('en');
            setTranslate(en);
            localStorage.setItem('language', 'en');
        }
    }

    const onMessage = useCallback((value) => {
        setMessage(value);
        setTimeout(() => {
            setMessage({ type: null, message: '' });
        }, 7000);
    }, []);

    return (
        <ErrorBoundary>
            <IntlProvider key={lang} locale={lang} messages={translate} defaultLocale={'ru'}>
                <Message value={message} />

                <MessageContext.Provider value={{ onMessage }}>
                    <AuthProvider>
                        {isPaymentLayout ? (
                            <React.Fragment>
                                {!isWidgetPage && <Header />}

                                <Switch>
                                    <Route path="/pay3" component={Pay3} />
                                    <Route path="/pay4" component={Pay4} />
                                    <Route path="/PayTransfer" component={PayTransfer} />
                                    <Route path="/npay/:token" component={NPay} />
                                    <Redirect to="/pay3" />
                                </Switch>
                            </React.Fragment>
                        ) : (
                            <Switch>
                                <Route
                                    path="/login"
                                    component={Login}
                                />
                                <Route
                                    path="/confirmation/:code"
                                    render={() => (
                                        <PublicLayout footerProps={footerProps}>
                                            <Route path="/confirmation/:code" component={Confirmation} />
                                        </PublicLayout>
                                    )}
                                />
                                <Route
                                    path="/widget"
                                    render={() => (
                                        <PublicLayout footerProps={footerProps}>
                                            <Route path="/widget" component={WidgetDocs} />
                                        </PublicLayout>
                                    )}
                                />
                                <Route
                                    exact
                                    path="/wiki"
                                    component={ApiV3DocsRedirect}
                                />
                                <Route
                                    path="/wiki/"
                                    render={() => (
                                        <PublicLayout footerProps={footerProps}>
                                            {lang === 'ru' ? <PublicPageRu /> : <PublicPageEn />}
                                        </PublicLayout>
                                    )}
                                />

                                <Route exact path="/" render={() => <UserRoutes footerProps={footerProps} />} />
                                <PrivateRout
                                    path={USER_CONTOUR_PATHS}
                                    component={(routeProps) => <UserRoutes {...routeProps} footerProps={footerProps} />}
                                />
                                <PrivateRout
                                    path={ADMIN_CONTOUR_PATHS}
                                    component={(routeProps) => <AdminRoutes {...routeProps} footerProps={footerProps} />}
                                />

                                <Redirect to="/" />
                            </Switch>
                        )}
                    </AuthProvider>
                </MessageContext.Provider>
            </IntlProvider>
        </ErrorBoundary>
    );
}

export default App;



// WEBPACK FOOTER //
// ./src/App.js